import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Modal,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

const MIN_WIDTH = 420;
const MIN_HEIGHT = 320;
const DEFAULT_WIDTH = 760;
const DEFAULT_HEIGHT = 560;
const VIEWPORT_MARGIN = 24;
const JSON_PARSE_DEPTH = 2;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getViewport() {
  if (typeof window === 'undefined') {
    return {
      width: 1440,
      height: 900,
    };
  }

  return {
    width: window.innerWidth,
    height: window.innerHeight,
  };
}

function buildInitialFrame() {
  const viewport = getViewport();
  const width = clamp(DEFAULT_WIDTH, MIN_WIDTH, viewport.width - VIEWPORT_MARGIN * 2);
  const height = clamp(DEFAULT_HEIGHT, MIN_HEIGHT, viewport.height - VIEWPORT_MARGIN * 2);
  const x = Math.max((viewport.width - width) / 2, VIEWPORT_MARGIN);
  const y = Math.max((viewport.height - height) / 2, VIEWPORT_MARGIN);

  return { x, y, width, height };
}

function tryParseJsonString(value) {
  if (typeof value !== 'string') {
    return { parsed: false, value };
  }

  const trimmed = value.trim();

  if (!trimmed) {
    return { parsed: false, value };
  }

  try {
    return {
      parsed: true,
      value: JSON.parse(trimmed),
    };
  } catch (error) {
    return { parsed: false, value };
  }
}

function normalizeInspectableValue(value, depth = 0) {
  const { parsed, value: parsedValue } = tryParseJsonString(value);

  if (parsed) {
    if (depth >= JSON_PARSE_DEPTH) {
      return parsedValue;
    }

    return normalizeInspectableValue(parsedValue, depth + 1);
  }

  if (Array.isArray(value)) {
    if (depth >= JSON_PARSE_DEPTH) {
      return value;
    }

    return value.map((entry) => normalizeInspectableValue(entry, depth + 1));
  }

  if (value && typeof value === 'object') {
    if (depth >= JSON_PARSE_DEPTH) {
      return value;
    }

    return Object.fromEntries(
      Object.entries(value).map(([key, entryValue]) => [key, normalizeInspectableValue(entryValue, depth + 1)])
    );
  }

  return value;
}

export default function DataInspector({ open, onClose, title, subtitle, data }) {
  const [frame, setFrame] = useState(buildInitialFrame);
  const dragStateRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    setFrame(buildInitialFrame());
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerMove = (event) => {
      const interaction = dragStateRef.current;

      if (!interaction) {
        return;
      }

      const viewport = getViewport();
      const deltaX = event.clientX - interaction.startX;
      const deltaY = event.clientY - interaction.startY;

      if (interaction.type === 'drag') {
        const nextX = clamp(interaction.initialFrame.x + deltaX, VIEWPORT_MARGIN, viewport.width - interaction.initialFrame.width - VIEWPORT_MARGIN);
        const nextY = clamp(interaction.initialFrame.y + deltaY, VIEWPORT_MARGIN, viewport.height - interaction.initialFrame.height - VIEWPORT_MARGIN);

        setFrame((prev) => ({
          ...prev,
          x: nextX,
          y: nextY,
        }));

        return;
      }

      if (interaction.type === 'resize-right') {
        const maxWidth = viewport.width - interaction.initialFrame.x - VIEWPORT_MARGIN;
        const nextWidth = clamp(interaction.initialFrame.width + deltaX, MIN_WIDTH, maxWidth);

        setFrame((prev) => ({
          ...prev,
          width: nextWidth,
        }));

        return;
      }

      if (interaction.type === 'resize-top') {
        const nextY = clamp(interaction.initialFrame.y + deltaY, VIEWPORT_MARGIN, interaction.initialFrame.y + interaction.initialFrame.height - MIN_HEIGHT);
        const nextHeight = clamp(interaction.initialFrame.height - deltaY, MIN_HEIGHT, viewport.height - nextY - VIEWPORT_MARGIN);

        setFrame((prev) => ({
          ...prev,
          y: nextY,
          height: nextHeight,
        }));
      }
    };

    const handlePointerUp = () => {
      dragStateRef.current = null;
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [open]);

  const normalizedData = useMemo(() => normalizeInspectableValue(data), [data]);
  const renderedContent = useMemo(() => {
    if (normalizedData === undefined) {
      return 'No data available.';
    }

    return JSON.stringify(normalizedData, null, 2);
  }, [normalizedData]);

  const beginInteraction = (type) => (event) => {
    event.preventDefault();
    event.stopPropagation();

    dragStateRef.current = {
      type,
      startX: event.clientX,
      startY: event.clientY,
      initialFrame: frame,
    };
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="data-inspector-title">
      <Box sx={{ position: 'fixed', inset: 0 }}>
        <Paper
          elevation={12}
          onClick={(event) => event.stopPropagation()}
          sx={{
            position: 'fixed',
            left: frame.x,
            top: frame.y,
            width: frame.width,
            height: frame.height,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: 2,
            outline: 'none',
          }}
        >
          <Box
            onPointerDown={beginInteraction('resize-top')}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 8,
              cursor: 'ns-resize',
              zIndex: 2,
            }}
          />
          <Box
            onPointerDown={beginInteraction('resize-right')}
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              width: 8,
              cursor: 'ew-resize',
              zIndex: 2,
            }}
          />

          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={2}
            onPointerDown={beginInteraction('drag')}
            sx={{
              px: 3,
              py: 2.5,
              cursor: 'move',
              userSelect: 'none',
              bgcolor: 'background.paper',
            }}
          >
            <Stack spacing={1} sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <DragIndicatorIcon fontSize="small" color="action" />
                <Typography id="data-inspector-title" variant="h6">
                  {title}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
              {/* <Chip
                size="small"
                variant="outlined"
                label={`Nested JSON parsing depth: ${JSON_PARSE_DEPTH}`}
                sx={{ alignSelf: 'flex-start' }}
              /> */}
            </Stack>

            <IconButton
              onClick={onClose}
              aria-label="Close data inspector"
              sx={{ cursor: 'pointer' }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>

          <Divider />

          <Box
            component="pre"
            sx={{
              flex: 1,
              m: 0,
              px: 3,
              py: 2.5,
              overflow: 'auto',
              bgcolor: 'background.default',
              fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
              fontSize: 12,
              lineHeight: 1.65,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {renderedContent}
          </Box>
        </Paper>
      </Box>
    </Modal>
  );
}

DataInspector.propTypes = {
  data: PropTypes.any,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  subtitle: PropTypes.string,
  title: PropTypes.string,
};

DataInspector.defaultProps = {
  data: undefined,
  subtitle: 'Inspect raw backend payloads with nested JSON fields parsed for readability.',
  title: 'Data Inspector',
};
